const AUTH_TOKEN_KEY = "AUTH_TOKEN";
const ACTIVE_STUDENT_ID_KEY = "activeStudentId";
const ACTIVE_ACADEMIC_YEAR_KEY = "activeAcademicYear";
const PARENT_BOOTSTRAP_CACHE_KEY = "erp_parent_bootstrap_cache";
const AUTH_SESSION_SNAPSHOT_KEY = "erp_confirmed_session_snapshot_v1";
const ACTIVE_PORTAL_MODE_KEY = "erp_active_portal_mode";
const PARENT_BOOTSTRAP_CACHE_TTL_MS = 60 * 1000;
const LEGACY_TOKEN_KEYS = [
  "token",
  "auth",
  "jwt",
  "sessionToken",
  "access_token",
  "id_token",
];

const toTrimmed = (value) => String(value ?? "").trim();
const emitAuthTokenChange = () => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event("auth-token-changed"));
  } catch (error) {
    // no-op
  }
};

const readStorageValue = (key) => {
  if (typeof window === "undefined") return "";
  try {
    return toTrimmed(localStorage.getItem(key));
  } catch (error) {
    return "";
  }
};

const removeStorageValue = (key) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // no-op
  }
};

const readSessionStorageValue = (key) => {
  if (typeof window === "undefined") return "";
  try {
    return toTrimmed(window.sessionStorage.getItem(key));
  } catch (error) {
    return "";
  }
};

const writeSessionStorageValue = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    // no-op
  }
};

const removeSessionStorageValue = (key) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // no-op
  }
};

const readFrom = (storage, key) => {
  if (typeof window === "undefined" || !storage) return "";
  try {
    return toTrimmed(storage.getItem(key));
  } catch (error) {
    return "";
  }
};

const writeTo = (storage, key, value) => {
  if (typeof window === "undefined" || !storage) return;
  try {
    storage.setItem(key, value);
  } catch (error) {
    // no-op
  }
};

const removeFrom = (storage, key) => {
  if (typeof window === "undefined" || !storage) return;
  try {
    storage.removeItem(key);
  } catch (error) {
    // no-op
  }
};

export const readActiveStudentId = () =>
  readStorageValue(ACTIVE_STUDENT_ID_KEY);

export const readActiveAcademicYear = () =>
  readStorageValue(ACTIVE_ACADEMIC_YEAR_KEY);

export const normalizePortalMode = (value) => {
  const mode = toTrimmed(value).toLowerCase();
  return mode === "teacher" || mode === "management" ? mode : "";
};

export const readActivePortalMode = () =>
  normalizePortalMode(readSessionStorageValue(ACTIVE_PORTAL_MODE_KEY));

export const writeActivePortalMode = (mode) => {
  const normalized = normalizePortalMode(mode);
  if (normalized) {
    writeSessionStorageValue(ACTIVE_PORTAL_MODE_KEY, normalized);
  } else {
    removeSessionStorageValue(ACTIVE_PORTAL_MODE_KEY);
  }
  return normalized;
};

export const clearActivePortalMode = () => {
  removeSessionStorageValue(ACTIVE_PORTAL_MODE_KEY);
};

export const readAuthToken = () => {
  if (typeof window === "undefined") return "";
  const canonical = readFrom(window.localStorage, AUTH_TOKEN_KEY);
  if (canonical) return canonical;

  const legacyToken =
    LEGACY_TOKEN_KEYS.map((key) => readFrom(window.localStorage, key)).find(
      Boolean,
    ) ||
    LEGACY_TOKEN_KEYS.map((key) => readFrom(window.sessionStorage, key)).find(
      Boolean,
    );

  if (!legacyToken) return "";
  writeTo(window.localStorage, AUTH_TOKEN_KEY, legacyToken);
  LEGACY_TOKEN_KEYS.forEach((key) => {
    removeFrom(window.localStorage, key);
    removeFrom(window.sessionStorage, key);
  });
  return legacyToken;
};

export const writeAuthToken = (token) => {
  const value = toTrimmed(token);
  if (typeof window === "undefined") return value;
  if (value) {
    writeTo(window.localStorage, AUTH_TOKEN_KEY, value);
    LEGACY_TOKEN_KEYS.forEach((key) => {
      removeFrom(window.localStorage, key);
      removeFrom(window.sessionStorage, key);
    });
    emitAuthTokenChange();
  } else {
    removeFrom(window.localStorage, AUTH_TOKEN_KEY);
    emitAuthTokenChange();
  }
  return value;
};

export const clearAuthToken = () => {
  if (typeof window === "undefined") return;
  removeFrom(window.localStorage, AUTH_TOKEN_KEY);
  LEGACY_TOKEN_KEYS.forEach((key) => {
    removeFrom(window.localStorage, key);
    removeFrom(window.sessionStorage, key);
  });
  emitAuthTokenChange();
};

export const writeActiveStudentId = (studentId) => {
  const value = toTrimmed(studentId);
  if (typeof window === "undefined") return value;
  if (value) {
    localStorage.setItem(ACTIVE_STUDENT_ID_KEY, value);
  } else {
    removeStorageValue(ACTIVE_STUDENT_ID_KEY);
  }
  return value;
};

export const writeActiveAcademicYear = (academicYear) => {
  const value = toTrimmed(academicYear);
  if (typeof window === "undefined") return value;
  if (value) {
    localStorage.setItem(ACTIVE_ACADEMIC_YEAR_KEY, value);
  } else {
    removeStorageValue(ACTIVE_ACADEMIC_YEAR_KEY);
  }
  return value;
};

export const clearActiveStudentId = () => {
  removeStorageValue(ACTIVE_STUDENT_ID_KEY);
};

export const clearActiveAcademicYear = () => {
  removeStorageValue(ACTIVE_ACADEMIC_YEAR_KEY);
};

const parseJsonSafe = (value) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
};

const fingerprintToken = (token = "") => {
  let hash = 2166136261;
  const value = toTrimmed(token);
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return value ? `v1:${(hash >>> 0).toString(16)}` : "";
};

const pickConfirmedIdentity = (me = {}) => {
  if (!me || typeof me !== "object") return null;
  const keys = [
    "id",
    "_id",
    "userId",
    "studentId",
    "student_id",
    "loginId",
    "login_id",
    "displayName",
    "name",
    "studentName",
    "student_name",
    "firstName",
    "lastName",
    "class",
    "className",
    "section",
    "sectionName",
    "school_id",
    "schoolId",
    "underBy",
    "system_role",
    "systemRole",
    "management_role",
    "managementRole",
  ];
  return keys.reduce((result, key) => {
    if (me[key] !== undefined && me[key] !== null && me[key] !== "") {
      result[key] = me[key];
    }
    return result;
  }, {});
};

export const writeConfirmedAuthSnapshot = (authState = {}) => {
  const tokenFingerprint = fingerprintToken(readAuthToken());
  if (!tokenFingerprint || typeof window === "undefined") return null;
  const snapshot = {
    tokenFingerprint,
    confirmedAt: Date.now(),
    state: {
      me: pickConfirmedIdentity(authState.me || authState.user),
      user: pickConfirmedIdentity(authState.user || authState.me),
      school: authState.school || null,
      systemRole: toTrimmed(authState.systemRole),
      managementRole: toTrimmed(authState.managementRole),
      activePortal: toTrimmed(authState.activePortal),
      availablePortals: Array.isArray(authState.availablePortals)
        ? authState.availablePortals.slice(0, 3)
        : [],
      managementPortalRole: toTrimmed(authState.managementPortalRole),
      schoolId: toTrimmed(authState.schoolId),
      divisionScope: Array.isArray(authState.divisionScope)
        ? authState.divisionScope
        : [],
      classScope: Array.isArray(authState.classScope)
        ? authState.classScope
        : [],
      permissions: authState.permissions || {},
      rolePermissions: authState.rolePermissions || null,
      scope: authState.scope || null,
      tokenExpiresAt: toTrimmed(authState.tokenExpiresAt),
    },
  };
  writeSessionStorageValue(AUTH_SESSION_SNAPSHOT_KEY, JSON.stringify(snapshot));
  return snapshot;
};

export const readConfirmedAuthSnapshot = () => {
  if (typeof window === "undefined") return null;
  const parsed = parseJsonSafe(
    readSessionStorageValue(AUTH_SESSION_SNAPSHOT_KEY),
  );
  if (!parsed?.state || !parsed?.tokenFingerprint) return null;
  if (parsed.tokenFingerprint !== fingerprintToken(readAuthToken()))
    return null;
  return parsed;
};

export const clearConfirmedAuthSnapshot = () => {
  removeSessionStorageValue(AUTH_SESSION_SNAPSHOT_KEY);
};

export const readParentBootstrapCache = () => {
  if (typeof window === "undefined") return null;
  const raw = readSessionStorageValue(PARENT_BOOTSTRAP_CACHE_KEY);
  if (!raw) return null;
  const parsed = parseJsonSafe(raw);
  if (!parsed) return null;
  return parsed;
};

export const writeParentBootstrapCache = (payload = {}) => {
  if (typeof window === "undefined") return null;
  const next = {
    ...payload,
    cachedAt: Date.now(),
  };
  writeSessionStorageValue(PARENT_BOOTSTRAP_CACHE_KEY, JSON.stringify(next));
  return next;
};

export const clearParentBootstrapCache = () => {
  removeSessionStorageValue(PARENT_BOOTSTRAP_CACHE_KEY);
};

export const readFreshParentBootstrapCache = ({
  studentId = "",
  maxAgeMs = PARENT_BOOTSTRAP_CACHE_TTL_MS,
} = {}) => {
  const cache = readParentBootstrapCache();
  if (!cache) return null;
  const cachedAt = Number(cache.cachedAt || 0);
  if (!Number.isFinite(cachedAt) || cachedAt <= 0) return null;
  if (Date.now() - cachedAt > maxAgeMs) return null;
  const cachedStudentId = String(
    cache.studentId || cache.activeStudentId || "",
  ).trim();
  const requestedStudentId = String(studentId || "").trim();
  if (
    requestedStudentId &&
    cachedStudentId &&
    cachedStudentId !== requestedStudentId
  )
    return null;
  return cache;
};

export const hasParentPortalSession = () => {
  if (typeof window === "undefined") return false;
  const token = readAuthToken();
  const studentId = readActiveStudentId();
  return Boolean(token && studentId);
};

export const resolveStudentIdCandidate = (...values) => {
  for (const value of values) {
    const next = toTrimmed(value);
    if (next) return next;
  }
  return "";
};

export const resolveStudentIdFromStudentData = (
  studentData = {},
  explicitStudentId = "",
) =>
  resolveStudentIdCandidate(
    explicitStudentId,
    studentData?._id,
    studentData?.ID,
    studentData?.id,
    studentData?.studentId,
    studentData?.student_id,
    studentData?.student_code,
    studentData?.studentCode,
    studentData?.login_id,
    studentData?.loginId,
    studentData?.rollno,
  );

export const resolveAdminIdFromStudentData = (studentData = {}) => {
  const data =
    studentData && typeof studentData === "object" ? studentData : {};
  return String(
    data?.underBy ||
      data?.under_by ||
      data?.adminId ||
      data?.admin_id ||
      data?.admin?.id ||
      data?.school_id ||
      data?.schoolId ||
      data?.school?.id ||
      "",
  ).trim();
};

export const withActiveStudentIdentity = (
  studentData = {},
  explicitStudentId = "",
) => {
  const source =
    studentData && typeof studentData === "object" ? studentData : {};
  const studentId = resolveStudentIdFromStudentData(source, explicitStudentId);
  if (!studentId) return source;
  return {
    ...source,
    _id: studentId,
    id: studentId,
    studentId,
    student_id: studentId,
  };
};

export const clearPortalSessionStorage = () => {
  clearConfirmedAuthSnapshot();
  clearAuthToken();
  if (typeof window === "undefined") return;
  try {
    window.localStorage.clear();
  } catch (error) {
    // no-op
  }
  try {
    window.sessionStorage.clear();
  } catch (error) {
    // no-op
  }
  try {
    window.dispatchEvent(new Event("erp-auth-cleared"));
  } catch (error) {
    // Pending authenticated work is also invalidated by the page redirect.
  }
};

export const emitConfirmedLogoutSignal = ({
  role = "",
  studentId = "",
} = {}) => {
  if (typeof window === "undefined") return false;
  const payload = {
    type: "ERP_CONFIRMED_LOGOUT",
    studentId: toTrimmed(studentId),
    role: toTrimmed(role),
  };
  try {
    window.dispatchEvent(
      new CustomEvent("ERP_CONFIRMED_LOGOUT", { detail: payload }),
    );
  } catch (error) {
    // Browser event is best-effort; the React Native bridge message is the mobile signal.
  }
  const bridge = window.ReactNativeWebView;
  if (!bridge || typeof bridge.postMessage !== "function") return false;
  try {
    bridge.postMessage(JSON.stringify(payload));
    return true;
  } catch (error) {
    return false;
  }
};

export {
  AUTH_TOKEN_KEY,
  ACTIVE_STUDENT_ID_KEY,
  ACTIVE_ACADEMIC_YEAR_KEY,
  ACTIVE_PORTAL_MODE_KEY,
  PARENT_BOOTSTRAP_CACHE_KEY,
  AUTH_SESSION_SNAPSHOT_KEY,
};
